/* eslint-disable */
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { get, isEmpty, isArray } from 'lodash';
import moment from 'moment';
import {
  useInvalidateQueries,
} from 'src/api/APIHooks';
import Notifier from 'common/helpers/Notifier';
import { browserHistory } from 'common/utils/browserHistory';
import { useFeatureFlag } from 'common/hooks';
import { SurveyJSFormRenderer } from '@unite-us/ui-surveyjs2';
import {
  BaseCard,
  BaseCardBody,
} from '@unite-us/ui';
import { apiDefault } from 'src/api/config';
import {
  useElAssessment,
  EL_ASSESSMENT_QUERY_KEYS,
} from 'src/pages/facesheet/_id/eligibility/hooks/useEligibilityAssessments';
import {
  ELIGIBILITY_ASSESSMENT_STATUS,
  getEligibleServicesUtil,
  snomedIdentifiedNeedMapping,
} from 'src/pages/facesheet/_id/eligibility/utils';
import NotEligible from 'src/pages/facesheet/_id/eligibility/components/NotEligible';
import ContactTitle from './components/ContactTitle';
import './EligibilityAssessment.scss';
import {
  updateAssessment,
  USE_MY_SCREENINGS_KEY,
  USE_TEMPLATE_V2,
  useElAssessmentTemplateId,
  useTemplateV2,
  useFindConditions,
  getInitiativeConditionsModel,
} from 'src/pages/facesheet/_id/eligibility/hooks/eligibilityApi';
import {
  ELEMENT_DICTIONARY,
  OPTION_DICTIONARY,
} from './surveyjsTemplates/createElAssessTemplate';
import {
  renderIdentifiedNeeds,
  useRelatedScreening,
} from 'src/pages/facesheet/_id/eligibility/hooks/useRelatedScreening';

const SURVEY_PAGE_NUMBER = 1; // set to 0 for page1, 1 for page2, etc.
const DRAFT_AUTOSAVE_DEBOUNCE_MS = 2000;

/*
  Eligibility Assessment:
  - front-end form for eligibility assessment is created in SurveyJS
  - back-end form is fetched from Screenings 2.0 API, endpoint /templates/:id
  - The visibility rules are defined in the surveyjs form
  - question ids and question option ids come from the backend and are matches to surveyjs elements
*/

function buildResponses(survey, { relatedScreenId = null }) {
  const responses = [];
  const surveyPage = survey.pages[SURVEY_PAGE_NUMBER];

  const extractData = (element, name, value) => {
    const title = element.title;
    let displayValue = value ? value.toString() : '';
    let valuesId = [];
    let choicesCustom = [];
    // Array of elements
    if (!isEmpty(element.choices)) {
      if (!Array.isArray(value)) {
        value = [value];
      }
      if (Array.isArray(value)) {
        const choicesText = [];
        value.forEach((v) => {
          const choice = element.choices.find((c) => c.value === v);
          if (get(element, 'name') === 'qSelectScreening') {
            console.warn('(Question Previous Screening) Choice not found', { value, element, choices: element.choices });
            return;
          }
          if (!choice) {
            console.error('Choice not found', { value, element, choices: element.choices });
            return;
          }
          choicesText.push(choice.text);
          if (choice.uuid) {
            valuesId.push(choice.uuid);
          }
          if (choice.custom) {
            choicesCustom.push(choice.custom);
          }
        });
        displayValue = choicesText.join(', ');
      }
    }

    if (element.inputType === 'date') {
      displayValue = moment(value).format('MM/DD/YYYY');
    }

    const returnObj = {
      id: element.uuid || null,
      name,
      title,
      value: value ? value.toString() : '',
      valueId: isEmpty(valuesId) ? null : valuesId,
      displayValue,
      custom: choicesCustom,
      elementType: element.getType(),
    };

    if (get(element, 'name') === 'qSelectScreening') {
      value = relatedScreenId;
      returnObj.value = relatedScreenId || '';
    }

    return returnObj;
  };

  Object.keys(survey.data).forEach((elementName) => {
    const val = survey.data[elementName];
    const questionModel = survey.getQuestionByValueName(elementName);
    if (isEmpty(questionModel)) { return; }
    const {
      name,
      title,
      valueId,
      displayValue,
      custom,
      value,
      elementType,
    } = extractData(questionModel, elementName, val);
    
    responses.push({
      id: questionModel.uuid || null,
      name,
      title,
      valueId,
      value,
      displayValue,
      custom,
      elementType,
    });
  });

  surveyPage.elements.forEach((element) => {
    const isAlreadySaved = responses.find((r) => r.name === element.name);
    if (isAlreadySaved) { return; }
    if (!get(element, 'custom.includeInAnswers')) { return; }

    const {
      id,
      name,
      title,
      valueId,
      displayValue,
      custom,
      value,
      elementType,
    } = extractData(element, element.name, element.value);

    const item = {
      id,
      name,
      title,
      valueId,
      value,
      displayValue,
      custom,
      elementType,
    };
    responses.push(item);
  });

  return responses;
}

export function EligibilityAssessment(props) {
  const SURVEY_PAGE_NUMBER = 1; // set to 0 for page1, 1 for page2, etc.
  
  const invalidateQueries = useInvalidateQueries();
  const [relatedScreenId, setRelatedScreenId] = useState(null);
  const { relatedScreening, needs } = useRelatedScreening(relatedScreenId);

  const [state, setState] = useState({
    needs: [],
    relatedScreening: null,
    conditions: [],
    populations: [],
    serviceDuplication: [],
    count: 0,
  });

  const {
    contactId,
    setLoading,
    mode,
    assessmentId,
    employeeId,
    contactData,
    onContactClick,
  } = props;
  const updatedCookingSuppliesEligibilityEnabled = useFeatureFlag(
    'fe-41-ea-v5-updated-cooking-supplies-eligibility',
  );
  const eligibleHomeRemediationEnabled = useFeatureFlag(
    'fe-65-EA-eligible-home-remediation',
  );

  const inEditMode = mode === 'edit';
  const { data: assessment } = useElAssessment(
    assessmentId,
    { enabled: Boolean(inEditMode && assessmentId) }
  );
  const templateId = useElAssessmentTemplateId();
  const { data: templateData } = useTemplateV2({ templateId });
  const [formJSON, setFormJSON] = useState(null);
  const {
    conditions,
    populations,
    serviceDuplication,
    isLoading: isLoadingConditions,
  } = useFindConditions(contactId);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = {
      ...state,
      needs,
      relatedScreening,
      conditions,
      populations,
      serviceDuplication,
      setState,
    };
  }, [state, relatedScreening]);

  const surveyRef = useRef();
  const draftAutoSaveTimeoutRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  const isDraftAutoSaveDirtyRef = useRef(false);
  const isHydratingFormRef = useRef(false);
  const isLoading = isLoadingConditions || !formJSON;

  useEffect(() => () => {
    if (draftAutoSaveTimeoutRef.current) {
      clearTimeout(draftAutoSaveTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const template = get(templateData, 'surveyjsTemplate');
    if (!assessment || !template || !surveyRef) { return; }
    setFormJSON(template);
  }, [templateData, assessment]);

  const autoPopulateESMF = (populateESMF = true, relatedScreening, currentAssessment) => {
    const survey = surveyRef.current.pages[SURVEY_PAGE_NUMBER];
    const currentAssessmentQuestions = currentAssessment?.data?.screen?.questions || [];
    const currentClinicalConditions = currentAssessmentQuestions.find((q) => q.primary_text === ELEMENT_DICTIONARY.qClinicalCriteria);
    const currentEnhancedPopulations = currentAssessmentQuestions.find((q) => q.primary_text === ELEMENT_DICTIONARY.qEnhancedPopulations);
    const currentServiceDuplication = currentAssessmentQuestions.find((q) => q.primary_text === ELEMENT_DICTIONARY.qServiceDuplicationCheck);
    const conditions = stateRef.current.conditions || currentClinicalConditions;
    const populations = stateRef.current.populations || currentEnhancedPopulations;
    const serviceDuplication = stateRef.current.serviceDuplication || currentServiceDuplication;
    
    const relatedQuestions = get(relatedScreening, 'screen.questions', []);
    // auto populate difficulty walking
    const qrelatedDifficultyWalking = relatedQuestions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qDifficultyWalking,
    );
    const qDifficultyWalking = survey.getQuestionByName('qDifficultyWalking');
    if (qDifficultyWalking) {
      qDifficultyWalking.value = qrelatedDifficultyWalking?.answer?.value;
    }

    // auto populate difficulty dressing
    const qrelatedDifficultyDressing = relatedQuestions.find(
      (q) => q.primary_text === ELEMENT_DICTIONARY.qDifficultyDressing,
    );
    const qDifficultyDressing = survey.getQuestionByName('qDifficultyDressing');
    if (qDifficultyDressing) {
      qDifficultyDressing.value = qrelatedDifficultyDressing?.answer?.value;
    }

    if (!populateESMF) { return; }

    const shouldPopulateConditions = isEmpty(currentClinicalConditions?.answers) && conditions?.length > 0;
    const shouldPopulatePopulations = isEmpty(currentEnhancedPopulations?.answers) && populations?.length > 0;
    const shouldPopulateServiceDuplication = isEmpty(currentServiceDuplication?.answers) && serviceDuplication?.length > 0;
    // pre-populate all identified conditions/enhanced populations from ESMF
    if (shouldPopulateConditions) {
      survey.getQuestionByName('qClinicalCriteria').value = conditions;
    }
    if (shouldPopulatePopulations) {
      survey.getQuestionByName('qEnhancedPopulations').value = populations;
    }
    if (shouldPopulateServiceDuplication) {
      survey.getQuestionByName('qServiceDuplicationCheck').value = serviceDuplication;
    }

    const enableAndSetToESMF = (surveyElementName) => {
      try {
        const element = survey.getQuestionByName(surveyElementName);
        const choices = element.choices;
        const ESMF_OPTION_TEXT = 'ESMF';
        const esmfOption = choices.find((c) => c.value === ESMF_OPTION_TEXT);
        esmfOption.custom.disabled = false;
        element.choices = JSON.parse(JSON.stringify(choices));
        element.value = ESMF_OPTION_TEXT;
      } catch (error) {
        console.error('Error enabling and setting to ESMF', error, { surveyElementName});
      }
    };

    shouldPopulateConditions && conditions?.length && conditions.forEach((condition) => {
      if (condition === OPTION_DICTIONARY.chronicCondition) {
        enableAndSetToESMF('qChronicConditionsVerification');
      }
      if (condition === OPTION_DICTIONARY.asthma) {
        enableAndSetToESMF('qAsthmaVerification');
      }
      if (condition === OPTION_DICTIONARY.enternalOrParenteral) {
        enableAndSetToESMF('qNutritionTherapyVerification');
      }
      if (condition === OPTION_DICTIONARY.heatRelatedIllness) {
        enableAndSetToESMF('qHeatIllnessVerification');
      }
      if (condition === OPTION_DICTIONARY.coldRelatedIllness) {
        enableAndSetToESMF('qColdIllnessVerification');
      }
      if (condition === OPTION_DICTIONARY.atRiskThermo) {
        enableAndSetToESMF('qThermoregulationRiskVerification');
      }
    });

    shouldPopulatePopulations && populations?.length && populations.forEach((condition) => {
      if (condition === OPTION_DICTIONARY.medicaidHighUtilizer) {
        enableAndSetToESMF('qMedicaidVerification');
      }
      if (condition === OPTION_DICTIONARY.enrolledNYS) {
        enableAndSetToESMF('qHealthHomeVerification');
      }
      if (condition === OPTION_DICTIONARY.mentalHealth) {
        enableAndSetToESMF('qMentalHealthVerification');
      }
      if (condition === OPTION_DICTIONARY.idd) {
        enableAndSetToESMF('qIntellectualDisabilitiesVerification');
      }
      if (condition === OPTION_DICTIONARY.pregnantAndPostpartum) {
        enableAndSetToESMF('qPregnancyVerificationMethod');
      }
      if (condition === OPTION_DICTIONARY.highRiskChildUnderEighteen) {
        enableAndSetToESMF('qChildVerificationMethod');
      }
      if (condition === OPTION_DICTIONARY.individualsChronicReleased) {
        enableAndSetToESMF('qConditionIncarcerationVerification');
        enableAndSetToESMF('qIncarcerationChronicConditionVerification');
      }
      if (condition === OPTION_DICTIONARY.childNutrition) {
        enableAndSetToESMF('qChildNutritionVerification');
      }
      if (condition === OPTION_DICTIONARY.otherPopulations) {
        enableAndSetToESMF('qOtherPopulationsVerification');
      }
    });
  };

  const [showNotEligible,setShowNotEligible] = useState(false);
  const divRef = useRef(null); // Create a reference for a DOM element

  const handleSetSurvey = (survey) => {
    surveyRef.current = survey;
    surveyRef.current.currentPageNo = SURVEY_PAGE_NUMBER;
    setState({ ...stateRef.current, count: state.count + 1 });
  };

  let hasPassedEligibility = false;
  const hasPassedEligibilityFn = () => {
    const survey = surveyRef.current.pages[SURVEY_PAGE_NUMBER];

    const qVerifyMedicaidEnrollment = survey.getQuestionByName('qVerifyMedicaidEnrollment');
    const qVerifyIdentity = survey.getQuestionByName('qVerifyIdentity');
    const hasVerifiedMemberIdentity = Boolean(qVerifyIdentity?.value === OPTION_DICTIONARY.oVerifiedMemberIdentity);
    const qConfirmAccurateInfo = survey.getQuestionByName('qConfirmAccurateInfo');
    const memberDoesAttest = Boolean(qConfirmAccurateInfo?.value === OPTION_DICTIONARY.oMemberDoesAttest);
    const qNYExcludedPopulation = survey.getQuestionByName('qNYExcludedPopulation');
    const isEnrolledMedicaidFFS = Boolean(qVerifyMedicaidEnrollment?.value === OPTION_DICTIONARY.oVerifyMedicaidEnrollmentFFS);
    const isEnrolledMedicaidMMC = Boolean(qVerifyMedicaidEnrollment?.value === OPTION_DICTIONARY.oVerifyMedicaidEnrollmentConfirmed);
    const isNotInExcludedPopulation = Boolean(
      qNYExcludedPopulation?.value === 'No',
    );
    return Boolean(
      isNotInExcludedPopulation
      && memberDoesAttest
      && hasVerifiedMemberIdentity
      && (isEnrolledMedicaidFFS || isEnrolledMedicaidMMC),
    );
  };

  const autoPopulateHousingSafetyConcerns = (survey, relatedScreening) => {
    const qHousingSafetyConcerns = survey.getQuestionByName('qHousingSafetyConcerns');
    const qHousingConcerns = survey.getQuestionByName('qHousingConcerns');

    const relatedScreeningQuestions = get(relatedScreening, 'screen.questions', []);
    const relatedHousingSafetyQuestion = relatedScreeningQuestions.find(
      (q) => q.primary_text === 'Think about the place you live. Do you have problems with any of the following?',
    );
    const relatedHousingSafetyAnswers = relatedHousingSafetyQuestion?.answers || [];
    const relatedHousingSafetyAnswerList = relatedHousingSafetyAnswers
      .map(
        (answer) => {
          const value = answer.value;
          if (value === 'Pests such as bugs, ants, or mice') {
            return 'Bugs or rodents like mice or rats';
          }
          if (value === 'Mold') {
            return 'Mold or mildew is present';
          }
          if (value === 'Lead paint or pipes') {
            return 'Possible lead paint or pipes';
          }
          if (value === 'Lack of heat') {
            return 'No heating or cooling when needed';
          }
          if (value === 'Oven or stove not working') {
            return 'Kitchen or cooking equipment is inadequate';
          }
          if (value === 'Smoke detectors missing or not working') {
            return 'Smoke detectors dont work';
          }
          if (value === 'Water leaks') {
            return 'Water leaks';
          }
          return null;
        })
        .filter((a) => a);
    
    if (
      qHousingSafetyConcerns?.value === 'Yes'
      && !qHousingConcerns.value.length
      && relatedHousingSafetyAnswerList.length
    ) {
      qHousingConcerns.value = relatedHousingSafetyAnswerList;
    }
  };

  const autoPopulateLivingSituation = (survey, relatedScreening, showFollowup) => {
    const qLivingSituation = survey.getQuestionByName('qLivingSituation');
    const relatedScreeningQuestions = get(relatedScreening, 'screen.questions', []);

    const relatedLivingSituationQuestion = relatedScreeningQuestions.find(
      (q) => q.primary_text === 'What is your living situation today?',
    );
    const relatedLivingSituationAnswer = relatedLivingSituationQuestion?.answer?.value;

    // left side is the AHC answer, right side is the EL assessment answer
    const MAPPING = {
      // 'I have a steady place to live': OPTION_DICTIONARY.oStablePlaceToLive, // do not map this
      'I have a place to live today, but I am worried about losing it in the future': OPTION_DICTIONARY.oPlaceToLiveWithProblems,
      'I do not have a steady place to live': OPTION_DICTIONARY.oNoPlaceToLive,
    };
    const populateValue = relatedLivingSituationAnswer && MAPPING[relatedLivingSituationAnswer] ?
      MAPPING[relatedLivingSituationAnswer] : null;
    if (
      !qLivingSituation?.value
      && showFollowup
      && populateValue
    ) {
      qLivingSituation.value = populateValue;
    }
  };

  const autoPopulateSchoolTraining = (survey, relatedScreening) => {
    const relatedScreeningQuestions = get(relatedScreening, 'screen.questions', []);

    const relatedQuestion = relatedScreeningQuestions.find(
      (q) => q.primary_text === 'Do you want help with school or training? For example, starting or completing job training or getting a high school diploma, GED or equivalent.',
    );
    const relatedAnswer = relatedQuestion?.answer?.value;

    const qSchoolTrainingHelp = survey.getQuestionByName('qSchoolTrainingHelp');
    if (qSchoolTrainingHelp && !qSchoolTrainingHelp.value) {
      qSchoolTrainingHelp.value = relatedAnswer;
    }
  };

  const onValueChanged = (sender, options, checkAll = false) => {
    const isUserTriggeredChange = !checkAll && Boolean(options?.name || options?.question?.name);
    if (isUserTriggeredChange) {
      if (isHydratingFormRef.current) {
        return;
      }
      scheduleDraftAutoSave();
    }

    try {
      const elementChanged = options?.name;

      const needsList = stateRef.current.needs;
      const relatedScreening = stateRef.current.relatedScreening;
      const state = stateRef.current;
      const survey = surveyRef.current.pages[SURVEY_PAGE_NUMBER];
      const surveyCurrent = surveyRef.current;
      const elementName = checkAll ? null : options.name;

      const qVerifyIdentity = survey.getQuestionByName('qVerifyIdentity');

      const qListAdditionalNameAndDOB = survey.getQuestionByName('qListAdditionalNameAndDOB');
      const qDifficultyWalking = survey.getQuestionByName('qDifficultyWalking');
      const qDifficultyDressing = survey.getQuestionByName('qDifficultyDressing');
      const qVerifyMedicaidEnrollment = survey.getQuestionByName('qVerifyMedicaidEnrollment');
      const hVerificationMedicaidEnrollment = survey.getQuestionByName('hVerificationMedicaidEnrollment');
      const qNYExcludedPopulation = survey.getQuestionByName('qNYExcludedPopulation');

      const hasVerifiedMemberIdentity = Boolean(qVerifyIdentity?.value === OPTION_DICTIONARY.oVerifiedMemberIdentity);
      
      const isEnrolledMedicaidFFS = Boolean(qVerifyMedicaidEnrollment?.value === OPTION_DICTIONARY.oVerifyMedicaidEnrollmentFFS);
      const isEnrolledMedicaidMMC = Boolean(qVerifyMedicaidEnrollment?.value === OPTION_DICTIONARY.oVerifyMedicaidEnrollmentConfirmed);
      const isUnverifiedEnrollmentMedicaid = Boolean(qVerifyMedicaidEnrollment?.value === OPTION_DICTIONARY.oVerifyMedicaidEnrollmentUnconfirmed);

      const qConfirmAccurateInfo = survey.getQuestionByName('qConfirmAccurateInfo');
      const isUnverifiedMember = Boolean(qVerifyIdentity?.value === OPTION_DICTIONARY.oUnverifiedMemberIdentity);
      const memberAttestNoAnswer = Boolean(qConfirmAccurateInfo?.value === undefined);
      const memberDoesNotAttest = Boolean(qConfirmAccurateInfo?.value === OPTION_DICTIONARY.oMemberDoesNotAttest);
      const memberDoesAttest = Boolean(qConfirmAccurateInfo?.value === OPTION_DICTIONARY.oMemberDoesAttest);
      const hStepTwo = survey.getQuestionByName('hStepTwo');
      const hStepTwoOne = survey.getQuestionByName('hStepTwo1');
      const showStepTwo = memberAttestNoAnswer || memberDoesAttest;
      qVerifyIdentity.visible = showStepTwo;
      hStepTwo.visible = showStepTwo;
      hStepTwoOne.visible = showStepTwo;

      hVerificationMedicaidEnrollment.visible = hasVerifiedMemberIdentity;
      qVerifyMedicaidEnrollment.visible = hasVerifiedMemberIdentity;

      const hasVerifiedIdentityAndMedicaidEnrollment = hasVerifiedMemberIdentity && (
        isEnrolledMedicaidFFS || isEnrolledMedicaidMMC
      );

      qNYExcludedPopulation.visible = hasVerifiedIdentityAndMedicaidEnrollment;

      if (!isUnverifiedMember
        && !memberDoesNotAttest
        && !isUnverifiedEnrollmentMedicaid
      ) {
        if (Boolean(qNYExcludedPopulation?.value === 'Yes')) {
          setShowNotEligible('one');
        }
        if (Boolean(qNYExcludedPopulation?.value === 'Unknown')) {
          setShowNotEligible('two');
        }
        if (Boolean(qNYExcludedPopulation?.value === 'No')) {
          setShowNotEligible(false);
        }
      }

      const showFollowup = hasPassedEligibilityFn();

      if (!hasPassedEligibility && showFollowup) {
        hasPassedEligibility = true;
        setState({ ...state, showFollowup: true });
        autoPopulateESMF(true, relatedScreening, assessment);
        return onValueChanged(null, null, true);
      }
      if (hasPassedEligibility && !showFollowup) {
        hasPassedEligibility = false;
        setState({ ...state, showFollowup: false });
        return onValueChanged(null, null, true);
      }
      
      const qAreYouCurrentlyEmployed = survey.getQuestionByName('qAreYouCurrentlyEmployed');
      const qReceiveTANF = survey.getQuestionByName('qReceiveTANF');
      const qImmediateFamily = survey.getQuestionByName('qImmediateFamily');
      const qImmediateFamilyMedicaid = survey.getQuestionByName('qImmediateFamilyMedicaid');
      const qIncomeAnySource = survey.getQuestionByName('qIncomeAnySource');
      const qEnhancedPopulations = survey.getQuestionByName('qEnhancedPopulations');
      const qEnhancedPopulationsHtml = survey.getQuestionByName('hEnhancedPopulationsHtml');
      
      qIncomeAnySource.visible = showFollowup;
      qEnhancedPopulations.visible = showFollowup && isEnrolledMedicaidMMC;
      qEnhancedPopulationsHtml.visible = showFollowup && isEnrolledMedicaidMMC;
      qImmediateFamily.visible = showFollowup;
      qImmediateFamilyMedicaid.visible = showFollowup;
      qReceiveTANF.visible = showFollowup;
      qAreYouCurrentlyEmployed.visible = showFollowup;
      qListAdditionalNameAndDOB.visible = qImmediateFamilyMedicaid?.value > 0;

      qDifficultyWalking.visible = showFollowup;
      qDifficultyDressing.visible = showFollowup;

      const requireTransportationAssistance = showFollowup && needsList.includes('transportation');
      const requireFoodAssistance = showFollowup && needsList.includes('food');
      const requireHousingAssistance = showFollowup && needsList.includes('housing'); // NEW
      const requireUtilitiesAssistance = showFollowup && needsList.includes('utilities'); // NEW
      const requireEmploymentAssistance = showFollowup && needsList.includes('unemployment');
      const requireHousingUtilitiesAssistance = showFollowup && needsList.includes('housing'); // to be deprecated, maybe?
      const requireEducation = showFollowup && needsList.includes('education');
      const isNeedsHeaderVisible = Boolean(
        requireTransportationAssistance ||
        requireFoodAssistance ||
        requireHousingAssistance ||
        requireUtilitiesAssistance ||
        requireEmploymentAssistance
      );

      const qNavigatorNotes = survey.getQuestionByName('qNavigatorNotes');
      const hConflictOfInterestHtml = survey.getQuestionByName('hConflictOfInterestHtml');
      qNavigatorNotes.visible = showFollowup;
      hConflictOfInterestHtml.visible = showFollowup;
      survey.getQuestionByName('hStepSix').visible = isNeedsHeaderVisible;

      // start of transportation questions
      if (elementName === 'qTransportationAssistance' || checkAll) {
        survey.getQuestionByName('hTransportation').visible = requireTransportationAssistance;
        
        const transportationQuestions = [
          'qTransportationAccess',
          'qTransportationServicesReceived',
          'qTypesOfActivitiesTransportation',
        ];
        for (let i = 0; i < transportationQuestions.length; i++) {
          survey.getQuestionByName(transportationQuestions[i]).visible = Boolean(requireTransportationAssistance);
        }
      }
      const enhancedPopSelected = qEnhancedPopulations.value;

      const customRequiredConditional =
        Boolean(
          isEnrolledMedicaidMMC
          && (
            enhancedPopSelected?.includes(OPTION_DICTIONARY.medicaidHighUtilizer)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.enrolledNYS)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.pregnantAndPostpartum)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.highRiskChildUnderEighteen)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.individualsChronicReleased)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.childNutrition)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.otherPopulations)
            || enhancedPopSelected?.includes(OPTION_DICTIONARY.idd)
          )
        );
      const qTypesOfActivitiesTransportation = survey.getQuestionByName('qTypesOfActivitiesTransportation');
      const qTransportationServicesReceived = survey.getQuestionByName('qTransportationServicesReceived');
      
      qTypesOfActivitiesTransportation.isRequired = customRequiredConditional;
      qTransportationServicesReceived.isRequired = customRequiredConditional

      const qTransportationAccess = survey.getQuestionByName('qTransportationAccess');
      const qTransportationHelp = survey.getQuestionByName('qTransportationHelp');
      const qTransportationTroubleReasons = survey.getQuestionByName('qTransportationTroubleReasons');

      qTransportationTroubleReasons.visible = Boolean(qTransportationAccess?.value === 'Yes');
      qTransportationHelp.visible = Boolean(qTransportationAccess?.value === 'Yes');

      const qTransportationServicesDescription = survey.getQuestionByName('qTransportationServicesDescription');

      qTransportationServicesDescription.visible = Boolean(qTransportationServicesReceived?.value === 'Yes');
      // end of transportation questions

      // start of food questions
        survey.getQuestionByName('hFood').visible = requireFoodAssistance;
        const foodQuestions = [
          'qFoodAssistanceType',
          'qFoodPreparationAbility',
          'qCanPrepareMeals',
          'qCaregiverPrepareMeals',
          'qNutritionalCounseling',
          'qWICProgram',
          'qSNAPBenefits',
          'qHomeDeliveredMeals',
          'qDoYouHaveCookingSupplies',
          'qDoYouHaveDietaryRestrictions',
          'qHowManyPeopleNeedFoodAssistance',
        ];
        for (let i = 0; i < foodQuestions.length; i++) {
          survey.getQuestionByName(foodQuestions[i]).visible = Boolean(requireFoodAssistance);
        }
      const qFoodPreparationAbility = survey.getQuestionByName('qFoodPreparationAbility');
      const qDoYouHaveCookingSupplies = survey.getQuestionByName('qDoYouHaveCookingSupplies');
      const qDoYouHaveDietaryRestrictions = survey.getQuestionByName('qDoYouHaveDietaryRestrictions');
      const qHowManyPeopleNeedFoodAssistance = survey.getQuestionByName('qHowManyPeopleNeedFoodAssistance');
      const qCanPrepareMeals = survey.getQuestionByName('qCanPrepareMeals');
      const qCaregiverPrepareMeals = survey.getQuestionByName('qCaregiverPrepareMeals');
      const qNutritionalCounseling = survey.getQuestionByName('qNutritionalCounseling');
      const qHomeDeliveredMeals = survey.getQuestionByName('qHomeDeliveredMeals');

      qFoodPreparationAbility.isRequired = customRequiredConditional;
      qDoYouHaveCookingSupplies.isRequired = customRequiredConditional;
      qDoYouHaveDietaryRestrictions.isRequired = customRequiredConditional;
      qHowManyPeopleNeedFoodAssistance.isRequired = customRequiredConditional;
      qCanPrepareMeals.isRequired = customRequiredConditional;
      qCaregiverPrepareMeals.isRequired = customRequiredConditional;
      qNutritionalCounseling.isRequired = customRequiredConditional;
      qHomeDeliveredMeals.isRequired = customRequiredConditional;
      // end of food questions

      // start of housing and utilities questions
      if (elementName === 'qHousingUtilitiesAssistance' || checkAll) {
        survey.getQuestionByName('hHousingUtilities').visible = Boolean(requireHousingUtilitiesAssistance);
        const housingUtilitiesQuestions = [
          'qLivingSituation',
          'qHousingProvider',
          'qHousingWaitlists',
          'qUtilitiesIssues',
          'qCareRecoveryNeed',
          'qHealthyHomesProgram',
        ];

        for (let i = 0; i < housingUtilitiesQuestions.length; i++) {
          survey.getQuestionByName(housingUtilitiesQuestions[i]).visible = Boolean(requireHousingUtilitiesAssistance);
        }
      }
      const qCareRecoveryNeed = survey.getQuestionByName('qCareRecoveryNeed');
      const qHospitalRisk = survey.getQuestionByName('qHospitalRisk');

      qHospitalRisk.visible = Boolean(Boolean(qCareRecoveryNeed?.value === 'Yes'));
      const qHousingProvider = survey.getQuestionByName('qHousingProvider');
      qHousingProvider.isRequired = customRequiredConditional;

      const qUtilitiesIssues = survey.getQuestionByName('qUtilitiesIssues');
      qUtilitiesIssues.visible = isNeedsHeaderVisible && Boolean(requireUtilitiesAssistance);
      const qUtilitiesIssuesTypes = survey.getQuestionByName('qUtilitiesIssuesTypes');
      const qUtilitiesAssistanceType = survey.getQuestionByName('qUtilitiesAssistanceType');
      const qUtilitiesProgramsReceived = survey.getQuestionByName('qUtilitiesProgramsReceived');

      if (qUtilitiesIssuesTypes) {
        qUtilitiesIssuesTypes.visible = Boolean(qUtilitiesIssues?.value === 'Yes');
      }
      if (qUtilitiesAssistanceType) {
        qUtilitiesAssistanceType.visible = Boolean(qUtilitiesIssues?.value === 'Yes');
      }
      if (qUtilitiesProgramsReceived) {
        qUtilitiesProgramsReceived.visible = Boolean(qUtilitiesIssues?.value === 'Yes');
      }

      const qUtilitiesProgramsDescription = survey.getQuestionByName('qUtilitiesProgramsDescription');
      if (qUtilitiesProgramsDescription) {
        qUtilitiesProgramsDescription.visible = Boolean(qUtilitiesProgramsReceived?.value === 'Yes');
      }

      const qLivingSituation = survey.getQuestionByName('qLivingSituation');
      if (elementName === 'qLivingSituation' || checkAll) {
        const qLivingValue = qLivingSituation?.value;
        const hasLivingSituationSelected = Boolean(qLivingValue);
        const hasSafeHousing = qLivingValue === "I have a stable and safe place to live with no problems.";
        survey.getQuestionByName('qHousingIssues').visible = Boolean(hasLivingSituationSelected && !hasSafeHousing);
      }
      const qHousingSafetyConcerns = survey.getQuestionByName('qHousingSafetyConcerns');
      qHousingSafetyConcerns.visible = requireHousingAssistance;

      if (checkAll) {
        autoPopulateLivingSituation(survey, relatedScreening, showFollowup);
      }

      qHousingSafetyConcerns.visible = Boolean(requireHousingAssistance);
      const qHousingConcerns = survey.getQuestionByName('qHousingConcerns');
      qHousingConcerns.visible = Boolean(qHousingSafetyConcerns?.value === 'Yes');
      // end of housing and utilities questions

      if (elementChanged === 'qHousingSafetyConcerns') {
        autoPopulateHousingSafetyConcerns(survey, relatedScreening);
      }

      // start of employment questions
      survey.getQuestionByName('hEmployment').visible = Boolean(requireEmploymentAssistance);
      const qEmploymentSituation = survey.getQuestionByName('qEmploymentSituation');
      qEmploymentSituation.visible = Boolean(requireEmploymentAssistance);

      const qIncomeOtherSource = survey.getQuestionByName('qIncomeOtherSource');
      qIncomeOtherSource.visible = Boolean(
        qEmploymentSituation?.value === OPTION_DICTIONARY.oEmployedNeedMoreHours
        || qEmploymentSituation?.value === OPTION_DICTIONARY.oEmployedAndSatisfied,
      );

      const qWorkInterest = survey.getQuestionByName('qWorkInterest');
      qWorkInterest.visible = Boolean(
        qEmploymentSituation?.value === OPTION_DICTIONARY.oNotCurrentlyEmployed
        || qEmploymentSituation?.value === OPTION_DICTIONARY.oEmployedNeedMoreHours
      );

      const qWhatOtherSourceIncome = survey.getQuestionByName('qWhatOtherSourceIncome');

      qWhatOtherSourceIncome.visible = Boolean(
        qIncomeOtherSource?.value === 'Yes',
      );
      // end of employment questions

      // start of education questions
      const qSchoolTrainingHelp = survey.getQuestionByName('qSchoolTrainingHelp');
      survey.getQuestionByName('hEducation').visible = Boolean(requireEducation);
      qSchoolTrainingHelp.visible = requireEducation;

      if (checkAll) {
        autoPopulateSchoolTraining(survey, relatedScreening);
      }
      // end of education questions

      // start of additional needs

      // end of additional needs

      // start of Clinical Criteria
      survey.getQuestionByName('hAdditionalNeeds').visible = showFollowup;
      survey.getQuestionByName('hClinicalCriteriaHtml').visible = showFollowup;

      const qChildcareHelp = survey.getQuestionByName('qChildcareHelp');
      qChildcareHelp.visible = showFollowup;

      const qClinicalCriteria = survey.getQuestionByName('qClinicalCriteria');
      const qChronicConditionsVerification = survey.getQuestionByName('qChronicConditionsVerification');
      const qChronicConditionsProviderName = survey.getQuestionByName('qChronicConditionsProviderName');
      const qChronicConditionsProviderNPI = survey.getQuestionByName('qChronicConditionsProviderNPI');
      const qChronicConditionsAttestationStored = survey.getQuestionByName('qChronicConditionsAttestationStored');

      const qAdditionalClinicalConditions = survey.getQuestionByName('qAdditionalClinicalConditions');
      qClinicalCriteria.visible = showFollowup;
      qAdditionalClinicalConditions.visible = showFollowup;

      // additional clinical condition enable/disable options
      const qRefrigerationMedication = survey.getQuestionByName('qRefrigerationMedication');
      const qRefrigerationBreastMilk = survey.getQuestionByName('qRefrigerationBreastMilk');
      qRefrigerationBreastMilk.visible = showFollowup;
      qRefrigerationMedication.visible = showFollowup;

      const qHousingSafetyConcernsValues = survey.getQuestionByName('qHousingSafetyConcerns');
      qAdditionalClinicalConditions.choices = qAdditionalClinicalConditions.custom.originalChoices.filter((choice) => {
        if (choice.value === OPTION_DICTIONARY.qDisabilityIndependence) {
          return Boolean(
            qLivingSituation?.value?.includes(OPTION_DICTIONARY.oPlaceToLiveWithProblems)
            && (
              qDifficultyWalking?.value === 'Yes'
              || qDifficultyDressing?.value === 'Yes'
            )
          );
        }
        if (choice.value === OPTION_DICTIONARY.qHealthConditionEnvironment) {
          return Boolean(qHousingSafetyConcernsValues?.value === 'Yes');
        }
        if (
          choice.value === OPTION_DICTIONARY.oAsthmaInpatient12mo
          || choice.value === OPTION_DICTIONARY.oAsthmaEDVisits12mo
          || choice.value === OPTION_DICTIONARY.oAsthmaSteroidRx12mo
          || choice.value === OPTION_DICTIONARY.oAsthmaInhalerRx12mo
          || choice.value === OPTION_DICTIONARY.oAsthmaUrgentCare12mo
        ) {
          return Boolean(
            qClinicalCriteria?.value?.includes(OPTION_DICTIONARY.asthma)
          );
        }
        if (choice.value === OPTION_DICTIONARY.qRefrigeratedMedication) {
          return Boolean(
            qRefrigerationMedication?.value === 'Yes'
            && qClinicalCriteria?.value?.includes(OPTION_DICTIONARY.chronicCondition)
          );
        }
        if (choice.value === OPTION_DICTIONARY.qRefrigerationBreastMilk) {
          return Boolean(
            qRefrigerationBreastMilk?.value === 'Yes'
            && enhancedPopSelected?.includes(OPTION_DICTIONARY.pregnantAndPostpartum)
          );
        }
        if (
          choice.value === OPTION_DICTIONARY.qPreSurgicalCare
          || choice.value === OPTION_DICTIONARY.qAcuteCareHospitalization
          || choice.value === OPTION_DICTIONARY.qRecuperationCare
        ) {
          return Boolean(
            qCareRecoveryNeed?.value === 'Yes'
          );
        }
        if (choice.value === OPTION_DICTIONARY.qMedicaidServiceRisk) {
          return Boolean(
            qHospitalRisk?.value === 'Yes'
          );
        }
        return false;
      });

      const qDisabilityIndepVerification = survey.getQuestionByName('qDisabilityIndepVerification');
      qDisabilityIndepVerification.visible = Boolean(
        qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qDisabilityIndependence),
      );

      const qDisabilityIndepAttestation = survey.getQuestionByName('qDisabilityIndepAttestation');
      qDisabilityIndepAttestation.visible = Boolean(
        qDisabilityIndepVerification?.value === 'Provider Attestation',
      );

      const qDisabilityIndepProviderName = survey.getQuestionByName('qDisabilityIndepProviderName');
      const qDisabilityIndepProviderNPI = survey.getQuestionByName('qDisabilityIndepProviderNPI');
      qDisabilityIndepProviderName.visible = Boolean(qDisabilityIndepAttestation?.value === 'Yes');
      qDisabilityIndepProviderNPI.visible = Boolean(qDisabilityIndepAttestation?.value === 'Yes');

      const qHealthConditionEnvVerification = survey.getQuestionByName('qHealthConditionEnvVerification');
      qHealthConditionEnvVerification.visible = Boolean(
        qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qHealthConditionEnvironment),
      );

      const qHealthConditionEnvAttestation = survey.getQuestionByName('qHealthConditionEnvAttestation');
      qHealthConditionEnvAttestation.visible = Boolean(
        qHealthConditionEnvVerification?.value === 'Provider Attestation',
      );

      const qHealthConditionEnvProviderName = survey.getQuestionByName('qHealthConditionEnvProviderName');
      const qHealthConditionEnvProviderNPI = survey.getQuestionByName('qHealthConditionEnvProviderNPI');
      qHealthConditionEnvProviderName.visible = Boolean(qHealthConditionEnvAttestation?.value === 'Yes');
      qHealthConditionEnvProviderNPI.visible = Boolean(qHealthConditionEnvAttestation?.value === 'Yes');

      const qPreSurgicalVerification = survey.getQuestionByName('qPreSurgicalVerification');
      qPreSurgicalVerification.visible = Boolean(
        qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qPreSurgicalCare),
      );

      const qPreSurgicalAttestation = survey.getQuestionByName('qPreSurgicalAttestation');
      qPreSurgicalAttestation.visible = Boolean(
        qPreSurgicalVerification?.value === 'Provider Attestation',
      );

      const qPreSurgicalProviderName = survey.getQuestionByName('qPreSurgicalProviderName');
      const qPreSurgicalProviderNPI = survey.getQuestionByName('qPreSurgicalProviderNPI');
      qPreSurgicalProviderName.visible = Boolean(qPreSurgicalAttestation?.value === 'Yes');
      qPreSurgicalProviderNPI.visible = Boolean(qPreSurgicalAttestation?.value === 'Yes');

      const qAcuteCareVerification = survey.getQuestionByName('qAcuteCareVerification');
      qAcuteCareVerification.visible = Boolean(
        qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qAcuteCareHospitalization)
        || qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qMedicaidServiceRisk)
        || qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qRecuperationCare),
      );

      const qAcuteCareAttestation = survey.getQuestionByName('qAcuteCareAttestation');
      qAcuteCareAttestation.visible = Boolean(
        qAcuteCareVerification?.value === 'Provider Attestation',
      );

      const qAcuteCareProviderName = survey.getQuestionByName('qAcuteCareProviderName');
      const qAcuteCareProviderNPI = survey.getQuestionByName('qAcuteCareProviderNPI');
      qAcuteCareProviderName.visible = Boolean(qAcuteCareAttestation?.value === 'Yes');
      qAcuteCareProviderNPI.visible = Boolean(qAcuteCareAttestation?.value === 'Yes');

      const qRefrigeratedMedsVerification = survey.getQuestionByName('qRefrigeratedMedsVerification');
      qRefrigeratedMedsVerification.visible = Boolean(
        qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qRefrigeratedMedication),
      );

      const qBreastMilkFridgeVerify = survey.getQuestionByName('qBreastMilkFridgeVerify');
      qBreastMilkFridgeVerify.visible = Boolean(
        qAdditionalClinicalConditions?.value?.includes(OPTION_DICTIONARY.qRefrigerationBreastMilk),
      );

      const qBreastMilkFridgeMemberAttestation = survey.getQuestionByName('qBreastMilkFridgeMemberAttestation');
      qBreastMilkFridgeMemberAttestation.visible = Boolean(
        qBreastMilkFridgeVerify?.value === 'Member Attestation',
      );

      const qRefrigeratedMedsAttestation = survey.getQuestionByName('qRefrigeratedMedsAttestation');
      qRefrigeratedMedsAttestation.visible = Boolean(
        qRefrigeratedMedsVerification?.value === 'Provider Attestation',
      );

      const qRefrigeratedMedsProviderName = survey.getQuestionByName('qRefrigeratedMedsProviderName');
      const qRefrigeratedMedsProviderNPI = survey.getQuestionByName('qRefrigeratedMedsProviderNPI');
      qRefrigeratedMedsProviderName.visible = Boolean(qRefrigeratedMedsAttestation?.value === 'Yes');
      qRefrigeratedMedsProviderNPI.visible = Boolean(qRefrigeratedMedsAttestation?.value === 'Yes');

      const qClinicalCriteriaValues = get(qClinicalCriteria, 'value');

      qChronicConditionsVerification.visible = Boolean(qClinicalCriteriaValues.includes('Chronic Conditions'));
      qChronicConditionsAttestationStored.visible = qChronicConditionsVerification?.value === 'Provider Attestation';
      qChronicConditionsProviderName.visible = qChronicConditionsAttestationStored?.value === 'Yes';
      qChronicConditionsProviderNPI.visible = qChronicConditionsAttestationStored?.value === 'Yes';

      const qAsthmaVerification = survey.getQuestionByName('qAsthmaVerification');
      const qAsthmaProviderName = survey.getQuestionByName('qAsthmaProviderName');
      const qAsthmaProviderNPI = survey.getQuestionByName('qAsthmaProviderNPI');
      const qAsthmaAttestationStored = survey.getQuestionByName('qAsthmaAttestationStored');

      qAsthmaVerification.visible = Boolean(qClinicalCriteriaValues.includes('Uncontrolled Asthma'));
      qAsthmaAttestationStored.visible = Boolean(qAsthmaVerification?.value === 'Provider Attestation');
      qAsthmaProviderName.visible = Boolean(qAsthmaAttestationStored?.value === 'Yes');
      qAsthmaProviderNPI.visible = Boolean(qAsthmaAttestationStored?.value === 'Yes');

      const qNutritionTherapyVerification = survey.getQuestionByName('qNutritionTherapyVerification');
      const qNutritionTherapyProviderName = survey.getQuestionByName('qNutritionTherapyProviderName');
      const qNutritionTherapyProviderNPI = survey.getQuestionByName('qNutritionTherapyProviderNPI');
      const qNutritionTherapyAttestationStored = survey.getQuestionByName('qNutritionTherapyAttestationStored');

      qNutritionTherapyVerification.visible = Boolean(qClinicalCriteriaValues.includes(OPTION_DICTIONARY.oEnteralOrParenteralNutritionTherapy));
      qNutritionTherapyAttestationStored.visible = Boolean(qNutritionTherapyVerification?.value === 'Provider Attestation');
      qNutritionTherapyProviderName.visible = Boolean(qNutritionTherapyAttestationStored?.value === 'Yes');
      qNutritionTherapyProviderNPI.visible = Boolean(qNutritionTherapyAttestationStored?.value === 'Yes');

      const qHeatIllnessVerification = survey.getQuestionByName('qHeatIllnessVerification');
      const qHeatIllnessProviderName = survey.getQuestionByName('qHeatIllnessProviderName');
      const qHeatIllnessProviderNPI = survey.getQuestionByName('qHeatIllnessProviderNPI');
      const qHeatIllnessAttestationStored = survey.getQuestionByName('qHeatIllnessAttestationStored');
      const qHeatIllnessAttestHome = survey.getQuestionByName('qHeatIllnessAttestHome');

      const ccIncludesHeatRelatedIllness = Boolean(
        qClinicalCriteriaValues.includes(OPTION_DICTIONARY.heatRelatedIllness)
      );
      qHeatIllnessVerification.visible = ccIncludesHeatRelatedIllness;
      qHeatIllnessAttestHome.visible = ccIncludesHeatRelatedIllness;
      qHeatIllnessAttestationStored.visible = Boolean(qHeatIllnessVerification?.value === 'Provider Attestation');
      qHeatIllnessProviderName.visible = Boolean(qHeatIllnessAttestationStored?.value === 'Yes');
      qHeatIllnessProviderNPI.visible = Boolean(qHeatIllnessAttestationStored?.value === 'Yes');

      const qColdIllnessVerification = survey.getQuestionByName('qColdIllnessVerification');
      const qColdIllnessProviderName = survey.getQuestionByName('qColdIllnessProviderName');
      const qColdIllnessProviderNPI = survey.getQuestionByName('qColdIllnessProviderNPI');
      const qColdIllnessAttestationStored = survey.getQuestionByName('qColdIllnessAttestationStored');
      const qColdIllnessAttestHome = survey.getQuestionByName('qColdIllnessAttestHome');

      const ccIncludesColdRelatedIllness = Boolean(
        qClinicalCriteriaValues.includes(OPTION_DICTIONARY.coldRelatedIllness)
      );
      qColdIllnessVerification.visible = ccIncludesColdRelatedIllness;
      qColdIllnessAttestHome.visible = ccIncludesColdRelatedIllness;
      qColdIllnessAttestationStored.visible = Boolean(qColdIllnessVerification?.value === 'Provider Attestation');
      qColdIllnessProviderName.visible = Boolean(qColdIllnessAttestationStored?.value === 'Yes');
      qColdIllnessProviderNPI.visible = Boolean(qColdIllnessAttestationStored?.value === 'Yes');

      const qThermoregulationRiskVerification = survey.getQuestionByName('qThermoregulationRiskVerification');
      const qThermoregulationRiskProviderName = survey.getQuestionByName('qThermoregulationRiskProviderName');
      const qThermoregulationRiskProviderNPI = survey.getQuestionByName('qThermoregulationRiskProviderNPI');
      const qThermoregulationRiskAttestationStored = survey.getQuestionByName('qThermoregulationRiskAttestationStored');
      qThermoregulationRiskAttestationStored.visible = Boolean(qThermoregulationRiskVerification?.value === 'Provider Attestation');

      qThermoregulationRiskVerification.visible = Boolean(qClinicalCriteriaValues.includes(OPTION_DICTIONARY.atRiskThermo));
      qThermoregulationRiskProviderName.visible = Boolean(qThermoregulationRiskAttestationStored?.value === 'Yes');
      qThermoregulationRiskProviderNPI.visible = Boolean(qThermoregulationRiskAttestationStored?.value === 'Yes');
      // end of Clinical Criteria

      // start of NYS Service Duplication Check
      survey.getQuestionByName('hServiceDuplicationCheckHtml').visible = showFollowup;
      const qServiceDuplicationCheck = survey.getQuestionByName('qServiceDuplicationCheck');
      const qOtherPrograms = survey.getQuestionByName('qOtherPrograms');
      const qAdditionalPrograms = survey.getQuestionByName('qAdditionalPrograms');

      qServiceDuplicationCheck.visible = showFollowup;
      qOtherPrograms.visible = showFollowup;
      qAdditionalPrograms.visible = qOtherPrograms?.value === 'Yes';

      // end of NYS Service Duplication Check

      // start of Step 8
      survey.getQuestionByName('hStepEightHtml').visible = showFollowup;
      const qConnectToServices = survey.getQuestionByName('qConnectToServices');
      qConnectToServices.visible = showFollowup;
      // end of Step 8

      const isSelectedMedicaidUtilizer = enhancedPopSelected.includes(OPTION_DICTIONARY.medicaidHighUtilizer);
      const isSelectedNYS = enhancedPopSelected.includes(OPTION_DICTIONARY.enrolledNYS);
      const isSelectedPregnantPostpartum = enhancedPopSelected.includes(OPTION_DICTIONARY.pregnantAndPostpartum);
      const isSelectedHighRiskChild = enhancedPopSelected.includes(OPTION_DICTIONARY.highRiskChildUnderEighteen);
      const isSelectedChronicConditionIncarceration = enhancedPopSelected.includes(OPTION_DICTIONARY.individualsChronicReleased);
      const isSelectedChildNutrition = enhancedPopSelected.includes(OPTION_DICTIONARY.childNutrition);
      const isSelectedOtherPopulations = enhancedPopSelected.includes(OPTION_DICTIONARY.otherPopulations);
      const qMedicaidVerification = survey.getQuestionByName('qMedicaidVerification');
      if (qMedicaidVerification) {
        qMedicaidVerification.visible = Boolean(isSelectedMedicaidUtilizer);
      }
      const qMedicaidVerificationAttestationForm = survey.getQuestionByName('qMedicaidVerificationAttestationForm');
      qMedicaidVerificationAttestationForm.visible = Boolean(qMedicaidVerification?.value === 'Provider Attestation');

      const qMedicaidVerificationProviderName = survey.getQuestionByName('qMedicaidVerificationProviderName');
      const qMedicaidVerificationProviderNPI = survey.getQuestionByName('qMedicaidVerificationProviderNPI');
      qMedicaidVerificationProviderName.visible = Boolean(qMedicaidVerificationAttestationForm?.value === 'Yes');
      qMedicaidVerificationProviderNPI.visible = Boolean(qMedicaidVerificationAttestationForm?.value === 'Yes');

      const qHealthHomeVerification = survey.getQuestionByName('qHealthHomeVerification');
      qHealthHomeVerification.visible = Boolean(isSelectedNYS);

      const qHealthHomeVerificationAttestationForm = survey.getQuestionByName('qHealthHomeVerificationAttestationForm');
      qHealthHomeVerificationAttestationForm.visible = Boolean(qHealthHomeVerification?.value === 'Provider Attestation');
      const qHealthHomeVerificationProviderName = survey.getQuestionByName('qHealthHomeVerificationProviderName');
      const qHealthHomeVerificationProviderNPI = survey.getQuestionByName('qHealthHomeVerificationProviderNPI');
      qHealthHomeVerificationProviderName.visible = Boolean(qHealthHomeVerificationAttestationForm?.value === 'Yes');
      qHealthHomeVerificationProviderNPI.visible = Boolean(qHealthHomeVerificationAttestationForm?.value === 'Yes');
      const qPregnancyVerificationMethod = survey.getQuestionByName('qPregnancyVerificationMethod');

      const qChildVerificationMethod = survey.getQuestionByName('qChildVerificationMethod');
      const qConditionIncarcerationVerification = survey.getQuestionByName('qConditionIncarcerationVerification');
      const qOtherPopulationsVerification = survey.getQuestionByName('qOtherPopulationsVerification');

      const qIntellectualDisabilitiesVerification = survey.getQuestionByName('qIntellectualDisabilitiesVerification');
      const qIntellectualDisabilitiesAttestationForm = survey.getQuestionByName('qIntellectualDisabilitiesAttestation');
      const qIntellectualDisabilitiesProviderName = survey.getQuestionByName('qIntellectualDisabilitiesProviderName');
      const qIntellectualDisabilitiesProviderNPI = survey.getQuestionByName('qIntellectualDisabilitiesProviderNPI');

      qIntellectualDisabilitiesVerification.visible = Boolean(enhancedPopSelected.includes(OPTION_DICTIONARY.idd));
      qIntellectualDisabilitiesAttestationForm.visible = Boolean(qIntellectualDisabilitiesVerification?.value === 'Provider Attestation');
      qIntellectualDisabilitiesProviderName.visible = Boolean(qIntellectualDisabilitiesAttestationForm?.value === 'Yes');
      qIntellectualDisabilitiesProviderNPI.visible = Boolean(qIntellectualDisabilitiesAttestationForm?.value === 'Yes');
      
      const qOtherPopulationsVerificationAttestationForm = survey.getQuestionByName('qOtherPopulationsVerificationAttestationForm');
      const qOtherPopulationsVerificationProviderName = survey.getQuestionByName('qOtherPopulationsVerificationProviderName');
      const qOtherPopulationsVerificationProviderNPI = survey.getQuestionByName('qOtherPopulationsVerificationProviderNPI');

      qOtherPopulationsVerification.visible = Boolean(isSelectedOtherPopulations);
      qOtherPopulationsVerificationAttestationForm.visible = Boolean(qOtherPopulationsVerification?.value === 'Provider Attestation');

      qOtherPopulationsVerificationProviderName.visible = Boolean(qOtherPopulationsVerificationAttestationForm?.value === 'Yes');
      qOtherPopulationsVerificationProviderNPI.visible = Boolean(qOtherPopulationsVerificationAttestationForm?.value === 'Yes');

      const qChildNutritionVerification = survey.getQuestionByName('qChildNutritionVerification');
      const qChildNutritionVerificationProviderName = survey.getQuestionByName('qChildNutritionVerificationProviderName');
      const qChildNutritionVerificationProviderNPI = survey.getQuestionByName('qChildNutritionVerificationProviderNPI');
      const qChildNutritionVerificationAttestationForm = survey.getQuestionByName('qChildNutritionVerificationAttestationForm');

      qChildNutritionVerification.visible = Boolean(isSelectedChildNutrition);
      qChildNutritionVerificationAttestationForm.visible = Boolean(qChildNutritionVerification?.value === 'Provider Attestation');
      qChildNutritionVerificationProviderName.visible = Boolean(qChildNutritionVerificationAttestationForm?.value === 'Yes');
      qChildNutritionVerificationProviderNPI.visible = Boolean(qChildNutritionVerificationAttestationForm?.value === 'Yes');
      
      qPregnancyVerificationMethod.visible = Boolean(isSelectedPregnantPostpartum);
      
      const qAreYouPregnant = survey.getQuestionByName('qAreYouPregnant');
      qAreYouPregnant.visible = Boolean(isSelectedPregnantPostpartum);
      
      const qEstimatedDeliveryDate = survey.getQuestionByName('qEstimatedDeliveryDate');
      qEstimatedDeliveryDate.visible = Boolean(qAreYouPregnant?.value === 'Yes');

      const qPostpartumStartDate = survey.getQuestionByName('qPostpartumStartDate');
      qPostpartumStartDate.visible = Boolean(qAreYouPregnant?.value === 'No');

      qChildVerificationMethod.visible = Boolean(isSelectedHighRiskChild);  // ESMF option is not possible
      // pre-select the second option (Provider Attestation)
      // survey.getQuestionByName('qChildVerificationMethod').value = 'Provider Attestation';

      qConditionIncarcerationVerification.visible = Boolean(isSelectedChronicConditionIncarceration);
      survey.getQuestionByName('qConditionIncarcerationDocsStored').visible = Boolean(qConditionIncarcerationVerification?.value === 'Member Documentation');

      const qPregnancyValue = qPregnancyVerificationMethod?.value;
      const qPregnancyVerificationAttestationForm = survey.getQuestionByName('qPregnancyVerificationAttestationForm');
      qPregnancyVerificationAttestationForm.visible = Boolean(qPregnancyVerificationMethod?.value === 'Provider Attestation');
      const pregnancyAttestationYesSelected = Boolean(qPregnancyVerificationAttestationForm?.value === 'Yes');
      const memberAttestationSelected = Boolean(qPregnancyValue === 'Member Attestation');
      const qPregnancyVerificationProviderName = survey.getQuestionByName('qPregnancyVerificationProviderName');
      const qPregnancyVerificationProviderNPI = survey.getQuestionByName('qPregnancyVerificationProviderNPI');
      qPregnancyVerificationProviderName.visible = Boolean(pregnancyAttestationYesSelected);
      qPregnancyVerificationProviderNPI.visible = Boolean(pregnancyAttestationYesSelected);
      survey.getQuestionByName('qPregnancyMemberAttestation').visible = Boolean(memberAttestationSelected);

      const qChildValue = qChildVerificationMethod?.value;
      const providerAttestationSelectedB = Boolean(qChildValue === 'Provider Attestation');
      const qChildAttestationStored = survey.getQuestionByName('qChildAttestationStored');
      qChildAttestationStored.visible = Boolean(providerAttestationSelectedB);
      survey.getQuestionByName('qChildProviderName').visible = Boolean(qChildAttestationStored?.value === 'Yes');
      survey.getQuestionByName('qChildProviderNPI').visible = Boolean(qChildAttestationStored?.value === 'Yes');

      const qConditionValue = qConditionIncarcerationVerification?.value;
      const memberDocumentationSelected = Boolean(qConditionValue === 'Member Documentation');
      survey.getQuestionByName('qConditionIncarcerationDocsStored').visible = Boolean(memberDocumentationSelected);

      const qIncarcerationChronicConditionVerification = survey.getQuestionByName('qIncarcerationChronicConditionVerification');
      const qIncarcerationChronicConditionAttestationForm = survey.getQuestionByName('qIncarcerationChronicConditionAttestationForm');
      const qIncarcerationChronicConditionProviderName = survey.getQuestionByName('qIncarcerationChronicConditionProviderName');
      const qIncarcerationChronicConditionProviderNPI = survey.getQuestionByName('qIncarcerationChronicConditionProviderNPI');
      
      qIncarcerationChronicConditionVerification.visible = Boolean(isSelectedChronicConditionIncarceration);
      qIncarcerationChronicConditionAttestationForm.visible = Boolean(qIncarcerationChronicConditionVerification?.value === 'Provider Attestation');
      qIncarcerationChronicConditionProviderName.visible = Boolean(qIncarcerationChronicConditionAttestationForm?.value === 'Yes');
      qIncarcerationChronicConditionProviderNPI.visible = Boolean(qIncarcerationChronicConditionAttestationForm?.value === 'Yes');

      const allElements = surveyCurrent.pages[SURVEY_PAGE_NUMBER].elements;

      allElements.forEach((question) => {
        if (!question.visible && question.value !== undefined) {
          question.clearValue();
        }
      });
    } catch (error) {
      console.error("Error in onValueChanged:", error);
    }
  }

  const handleValueChanged = onValueChanged;

  const getPersonConditionsDataPayload = () => {
    try {
      const responses = buildResponses(surveyRef.current, {
        relatedScreenId,
      });
      const groupedCodes = {};
      responses.forEach((response) => {
        if (response?.custom?.length) {
          response.custom.forEach((answer) => {
            const snomedValue = get(answer, 'option.snomed', null);
            // longer than 4 to avoid empty and bad snomed codes
            // needs to be cleaned up in the backend
            if (snomedValue && snomedValue.length > 4) {
              const identifiedNeed = snomedIdentifiedNeedMapping[snomedValue];
              if (identifiedNeed) {
                if (!groupedCodes[identifiedNeed]) {
                  groupedCodes[identifiedNeed] = {
                    name: identifiedNeed,
                    onset: new Date().toISOString(),
                    codes: []
                  };
                }
                groupedCodes[identifiedNeed].codes.push({
                  code: snomedValue,
                  classification: 'snomed',
                });
              }
            }
          });
        }
      });
      return Object.values(groupedCodes);
    } catch (error) {
      console.error('Error in getPersonConditionSnomedFromAnswers', error);
      return null;
    }
  };

  const buildAnswersPayload = (relatedScreenId) => {
    const responses = buildResponses(surveyRef.current, {
      relatedScreenId,
    });

    const answers = responses
      .map((response) => {
        const elementName = response.name;
        const elementType = get(response, 'elementType', '');
        const inputType = get(response, 'inputType', '');
        if ([
          'qEstimatedDeliveryDate',
          'qPostpartumStartDate',
        ].includes(elementName)) {
          return {
            question_id: response.id,
            value: response.value ? moment(response.value).format('YYYY-MM-DDTHH:mm:ss[Z]') : '',
          };
        }

        if ([
          'qImmediateFamily',
          'qImmediateFamilyMedicaid',
          'qHowManyPeopleNeedFoodAssistance',
        ].includes(elementName)) {
          return {
            question_id: response.id,
            value: parseInt(response.value, 10) || null,
          };
        }

        let option_id = null;
        const optionsSelected = response.custom.map((c) => c.option.uuid || c.option.id);
        option_id = optionsSelected;

        if (elementType === 'text') {
          return {
            question_id: response.id,
            value: response.value || '',
          };
        }
        if (elementType === 'comment') {
          return {
            question_id: response.id,
            value: response.value || '',
          };
        }

        if (elementType === 'dropdown-multiselect') {
          option_id = Array.isArray(optionsSelected) ? optionsSelected : [optionsSelected];
        } else if (optionsSelected.length > 1) {
          option_id = optionsSelected;
        } else {
          option_id = optionsSelected[0];
        }

        if (!option_id) {
          option_id = '';
        }
        return {
          question_id: response.id,
          option_id,
        }
      });

    return answers;
  };

  const runDraftAutoSave = async () => {
    const status = get(assessment, 'data.screen.status', null);
    const isDraftMode = status === ELIGIBILITY_ASSESSMENT_STATUS.DRAFT;
    const relatedScreenIdForSave = relatedScreenId ?? get(assessment, 'data.screen.related_screen_id', null);
    if (!isDraftMode || !assessmentId || !surveyRef.current || !relatedScreenIdForSave) {
      return;
    }

    if (isAutoSavingRef.current) {
      isDraftAutoSaveDirtyRef.current = true;
      return;
    }

    isDraftAutoSaveDirtyRef.current = false;
    isAutoSavingRef.current = true;
    try {
      const answers = buildAnswersPayload(relatedScreenIdForSave);
      const eligible_services = getEligibleServicesUtil(assessment, needs);

      await updateAssessment({
        personId: contactId,
        employeeId,
        status,
        answers,
        related_screen_id: relatedScreenIdForSave,
        outreach_status: get(assessment, 'data.screen.outreach_status', 'success'),
        templateId,
        consent: 'accepted',
        assessmentId,
        eligible_services,
      });
    } catch (error) {
      console.error('EligibilityAssessment.runDraftAutoSave', error);
    } finally {
      isAutoSavingRef.current = false;
      if (isDraftAutoSaveDirtyRef.current) {
        scheduleDraftAutoSave();
      }
    }
  };

  const scheduleDraftAutoSave = () => {
    const status = get(assessment, 'data.screen.status', null);
    const relatedScreenIdForSave = relatedScreenId ?? get(assessment, 'data.screen.related_screen_id', null);
    if (status !== ELIGIBILITY_ASSESSMENT_STATUS.DRAFT || !relatedScreenIdForSave) { return; }

    if (draftAutoSaveTimeoutRef.current) {
      clearTimeout(draftAutoSaveTimeoutRef.current);
    }

    draftAutoSaveTimeoutRef.current = setTimeout(() => {
      runDraftAutoSave();
    }, DRAFT_AUTOSAVE_DEBOUNCE_MS);
  };

  const invalidateAllQueries = () => {
    invalidateQueries(USE_MY_SCREENINGS_KEY);
    invalidateQueries(USE_TEMPLATE_V2);
    invalidateQueries(EL_ASSESSMENT_QUERY_KEYS.ASSESSMENTS);
    invalidateQueries(EL_ASSESSMENT_QUERY_KEYS.ASSESSMENT);
    invalidateQueries(getInitiativeConditionsModel(contactId));
  };

  const submit = async () => {
    try {
      const survey = surveyRef.current;
      
      const surveyPage = survey.pages[SURVEY_PAGE_NUMBER];
      // validates form and displays errors/warnings on the UI for required questions
      const validateForm  = () => {
        let isFormValid = true;
        surveyPage.questions.forEach(function (question) {
          if (question.hasErrors(true)) {
            isFormValid = false;
          }
        });
        return isFormValid;
      };

      const isValid = validateForm();

      if (!isValid) {
        Notifier.dispatch(400, 'Please ensure all required information is provided.');
        return;
      }
      setLoading(true);

      const answers = buildAnswersPayload(relatedScreenId);

      const eligible_services = getEligibleServicesUtil(assessment, needs, {
        updatedCookingSuppliesEligibilityEnabled,
        eligibleHomeRemediationEnabled,
      });
      const snomedList = getPersonConditionsDataPayload();
      if (snomedList.length) {
        const snomedPayload = {
          data: snomedList,
          relationships: {
            person_conditions_source: {
              type: 'person_conditions_source',
              source_id: assessmentId,
              source_type: 'screening',
            }
          }
        };
        await apiDefault.post(`/people/${contactId}/person_conditions/batch_create`, snomedPayload);
      }
      const response = await updateAssessment({
        personId: contactId,
        employeeId,
        status: ELIGIBILITY_ASSESSMENT_STATUS.COMPLETE,
        answers,
        related_screen_id: relatedScreenId,
        outreach_status: 'success',
        templateId,
        consent: 'accepted',
        assessmentId,
        eligible_services,
      });

      invalidateAllQueries();

      const screenId = get(response, 'data.screen.id');
      setLoading(false);
      if (screenId) {
        Notifier.dispatch(200, 'Eligibility Assessment Complete')
        browserHistory.push(`/facesheet/${contactId}/eligibility/view/${screenId}`);
        return;
      }
      Notifier.dispatch(200, 'We ran into an issue, please try again.');
    } catch (error) {
      console.error('EligibilityAssessment.submit', error);
      invalidateAllQueries();
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    try {
      setLoading(true);
      const answers = buildAnswersPayload(relatedScreenId);
      const eligible_services = getEligibleServicesUtil(assessment, needs, {
        updatedCookingSuppliesEligibilityEnabled,
        eligibleHomeRemediationEnabled,
      });

      const response = await updateAssessment({
        personId: contactId,
        employeeId,
        status: ELIGIBILITY_ASSESSMENT_STATUS.DRAFT,
        answers,
        related_screen_id: relatedScreenId,
        outreach_status: 'success',
        templateId,
        consent: 'accepted',
        assessmentId,
        eligible_services,
      });

      invalidateAllQueries();

      const screenId = get(response, 'data.screen.id');
      setLoading(false);
      if (screenId) {
        browserHistory.push(`/facesheet/${contactId}/eligibility/view/${screenId}`);
        Notifier.dispatch(200, 'Eligibility Assessment Saved as Draft');
        return;
      }
      Notifier.dispatch(200, 'We ran into an issue, please try again.');
    } catch (error) {
      console.error('EligibilityAssessment.saveDraft', error);
      invalidateAllQueries();
      setLoading(false);
    }
  };

  const cancel = () => {
    browserHistory.push(`/facesheet/${contactId}/eligibility/all`);
  };

  const populateForm = async (assessment) => {
    try {
      const survey = surveyRef.current;
      const questionsWithAnswers = get(assessment, 'data.screen.questions');

      const surveyData = {};

      survey.pages[SURVEY_PAGE_NUMBER].elements.forEach((element) => {
        const elementType = element.getType();
        const found = questionsWithAnswers.find((q) => q.primary_text === element.title);
        if (!formJSON || !found || (!found.answer && !found.answers)) { return; }

        const value = get(found, 'answer.value', null);
        const rValues = get(found, 'answers', null);
        const values = rValues && rValues.length ? rValues.map((a) => a.value) : null;
        if (elementType === 'dropdown' && value) {
          surveyData[element.name] = value;
        }
        if (elementType === 'text' && value) {
          surveyData[element.name] = value;
        }
        if (elementType === 'comment' && value) {
          surveyData[element.name] = value;
        }
        if (elementType === 'dropdown-multiselect' && values) {
          surveyData[element.name] = values;
        }
      });

      Object.keys(surveyData).forEach((key) => {
        if (isArray(surveyData[key])) {
          return surveyData[key] = surveyData[key].map((s) => s.replace(/'/g, ''));
        }
        // TODO: for immediateFamily and immediateFamilyMedicaid, this comes back as int
        // Temporary fix: check if surveyData[key] is an integer and convert it to string
        if (Number.isInteger(surveyData[key])) {
          surveyData[key] = surveyData[key].toString();
        }

        if (surveyData[key]) {
          surveyData[key] = surveyData[key].replace(/'/g, '');
        }
      });
      if (surveyData.qEstimatedDeliveryDate) {
        surveyData.qEstimatedDeliveryDate = moment(surveyData.qEstimatedDeliveryDate).toDate();
      }
      if (surveyData.qPostpartumStartDate) {
        surveyData.qPostpartumStartDate = moment(surveyData.qPostpartumStartDate).toDate();
      }

      const related_screen_id = get(assessment, 'data.screen.related_screen_id', null);
      setRelatedScreenId(related_screen_id);
      isHydratingFormRef.current = true;
      survey.data = surveyData;
      handleValueChanged(null, null, true);
      setTimeout(() => {
        isHydratingFormRef.current = false;
      }, 0);
    } catch (error) {
      isHydratingFormRef.current = false;
    }
  };

  const scrn942ScreeningsResultsUpdatesEnabled = useFeatureFlag('SCRN-942-screenings-results-updates');
  useEffect(() => {
    // base conditions
    if (!surveyRef.current || !assessment) { return; }

    invalidateQueries(USE_MY_SCREENINGS_KEY);
    const assessmentRelatedScreenId = get(assessment, 'data.screen.related_screen_id');
    // related screening not available yet
    if (!relatedScreening && assessmentRelatedScreenId) {
      setRelatedScreenId(assessmentRelatedScreenId);
      return;
    }
    if (relatedScreening) {
      renderIdentifiedNeeds(scrn942ScreeningsResultsUpdatesEnabled, relatedScreening, 1);
    }
    // related screening and assessment are available, populate the form
    populateForm(assessment);
  }, [assessment, surveyRef, surveyRef?.current, relatedScreening, formJSON, state.count]);

  useEffect(() => {
    if (!contactId) { return; }
    invalidateQueries(getInitiativeConditionsModel(contactId));
    // on unmount
    return () => {
      invalidateQueries(getInitiativeConditionsModel(contactId));
    }
  }, []);

  if (!formJSON || isLoadingConditions) { return null; }

  return (
    <div className="eligibility-assessment">
      <BaseCard className="bg-white">
        <div
          className='border border-solid border-medium-border-blue border-l-0 border-r-0 border-t-0'
        >
          <div className="px-5 py-8">
            <ContactTitle
              contactData={contactData}
              onContactClick={onContactClick}
              title="Eligibility Assessment for"
            />
            <p className="text-md">This form is to determine eligibility for HRSN services, if the client is not eligible, you can proceed to send referrals for regular client services.</p>
          </div>
        </div>

        <div className="px-6">
          <BaseCardBody>
            <SurveyJSFormRenderer
              formData={formJSON}
              formSubmission={null}
              editMode={true}
              onSurveyModelSet={handleSetSurvey}
              onValueChanged={handleValueChanged}
            />

            {
              showNotEligible ? (
                <NotEligible variation={showNotEligible} />
              ) : null
            }

            <div
              className="flex justify-between items-center mb-6 mt-3"
            >
              <button
                onClick={cancel}
                className="h-12 mr-4 px-16 py-3 border border-solid border-light-border-blue rounded text-action-darker-blue">
                <span className="inline-block align-middle">Cancel</span>
              </button>

              <div className="flex space-x-4">
                <button
                  disabled={isLoading}
                  onClick={saveDraft}
                  className="h-12 px-16 py-3 border border-solid border-medium-border-blue rounded text-action-darker-blue"
                >
                  <span className="inline-block align-middle mr-2">
                    <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.9643 3.5L13 0.535714C12.7143 0.25 12.1786 0 11.7857 0H2.21429C1.25 0 0.5 0.785714 0.5 1.71429V14.2857C0.5 15.25 1.25 16 2.21429 16H14.7857C15.7143 16 16.5 15.25 16.5 14.2857V4.71429C16.5 4.32143 16.25 3.78571 15.9643 3.5ZM8.5 13.7143C7.21429 13.7143 6.21429 12.7143 6.21429 11.4286C6.21429 10.1786 7.21429 9.14286 8.5 9.14286C9.75 9.14286 10.7857 10.1786 10.7857 11.4286C10.7857 12.7143 9.75 13.7143 8.5 13.7143ZM11.9286 2.85714V6.42857C11.9286 6.67857 11.7143 6.85714 11.5 6.85714H3.21429C2.96429 6.85714 2.78571 6.67857 2.78571 6.42857V2.71429C2.78571 2.5 2.96429 2.28571 3.21429 2.28571H11.3571C11.4643 2.28571 11.5714 2.35714 11.6786 2.42857L11.7857 2.53571C11.8571 2.60714 11.9286 2.75 11.9286 2.85714Z" fill="#4467AB"/>
                    </svg>
                  </span>
                  <span className="inline-block align-middle">Save Draft</span>
                </button>
                
                <button
                  disabled={isLoading}
                  onClick={submit}
                  style={{ backgroundColor: '#0B61A4' }}
                  className="h-12 px-16 py-3 rounded text-white"
                >
                  <span className="inline-block align-middle text-white">
                    Submit Assessment
                  </span>
                </button>
              </div>

            </div>
          </BaseCardBody>
        </div>
      </BaseCard>
      <div ref={divRef} style={{ display: "none" }}></div>
    </div>
  );
}

EligibilityAssessment.propTypes = {
  setLoading: PropTypes.func.isRequired,
  contactId: PropTypes.string.isRequired,
  employeeId: PropTypes.string,
  mode: PropTypes.string,
  assessmentId: PropTypes.string,
  children: PropTypes.node,
  contactData: PropTypes.object,
  onContactClick: PropTypes.func,
};

EligibilityAssessment.defaultProps = {
  children: null,
};

export default EligibilityAssessment;
